import React, { useState } from 'react';
import { Modal } from '../../components/Modal';
import type { RfqItemDto, RfqDetailDto } from '../../lib/rfqApi';
import { API_BASE_URL } from '../../lib/apiClient';
import { AttachmentPreviewModal } from '../../components/AttachmentPreviewModal';
import { formatDateTime as fmtDateTime } from "../../utils/dateFormat";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  rfq?: RfqDetailDto | null;
  items: RfqItemDto[];
}

/**
 * Build full backend file URL from /files/... (same helper as detail screen)
 */
const buildAttachmentUrl = (fileUrl?: string | null): string | undefined => {
  if (!fileUrl) return undefined;
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }
  const base = API_BASE_URL.replace(/\/api\/?$/, '');
  return `${base}${fileUrl}`;
};

export const RfqItemsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  rfq,
  items,
}) => {
  const [previewAtt, setPreviewAtt] = useState<{ url: string; filename?: string } | null>(null);
  const modalTitle =
    rfq?.subject ||
    rfq?.rfqId ||
    (rfq?._id ? `RFQ ${rfq._id}` : 'RFQ');

  const attachments = rfq?.attachments ?? [];

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      maxWidthClass="max-w-5xl"
    >
      <div className="text-xs space-y-4">
        {/* Attachment bar (optional but very useful in full-screen view) */}
        {attachments.length > 0 && (
          <div className="border border-slate-200 rounded p-2 bg-slate-50">
            <div className="mb-1 font-semibold text-slate-600">
              Attachments
            </div>
            <div className="space-y-1">
              {attachments.map((att, idx) => {
                const url = buildAttachmentUrl(att.fileUrl);
                return (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="truncate">{att.filename}</span>
                    {url && (
                      <button
                        type="button"
                        onClick={() => setPreviewAtt({ url, filename: att.filename })}
                        className="text-primary-500 font-semibold hover:underline ml-2"
                      >
                        View
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Header grid */}
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-slate-200 rounded p-2">
            <div className="font-semibold mb-1">Destination</div>
            <div>{rfq?.customerName || '—'}</div>
            <div>{rfq?.vesselName || ''}</div>
          </div>

          <div className="border border-slate-200 rounded p-2">
            <div className="font-semibold mb-1">Ship Chandlers</div>
            <div>United Ship Chandlers</div>
            <div>TRINIDAD AND TOBAGO</div>
          </div>

          <div className="border border-slate-200 rounded p-2">
            <div className="font-semibold mb-1">RFQ Meta</div>
            <div>ID: {rfq?.rfqId || rfq?._id || '—'}</div>
            {rfq?.receivedAt && (
              <div className="mt-1 text-xs text-slate-500">
                Received: {fmtDateTime(rfq.receivedAt)}
              </div>
            )}
          </div>
        </div>

        {/* Items table */}
        <div className="border border-slate-200 rounded overflow-hidden">
          <table className="data-table data-table-dense">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-2 py-1 text-left w-12">Item</th>
                <th className="px-2 py-1 text-left w-24">Our Article</th>
                <th className="px-2 py-1 text-left">Descriptions</th>
                <th className="px-2 py-1 text-right w-20">Quantity</th>
                <th className="px-2 py-1 text-left w-16">Unit</th>
                <th className="px-2 py-1 text-right w-28">Split Qty</th>
                <th className="px-2 py-1 text-left w-16">Split UOM</th>
                <th className="px-2 py-1 text-left w-28">Split Formula</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id} className="border-b border-slate-100">
                  <td className="px-2 py-1">{item.itemId}</td>
                  <td className="px-2 py-1 text-slate-500">—</td>
                  <td className="px-2 py-1">{item.description}</td>
                  <td className="px-2 py-1 text-right">
                    {item.quantity ?? ''}
                  </td>
                  <td className="px-2 py-1">{item.uom}</td>
                  <td className="px-2 py-1 text-right">{item.splitQuantity ?? ''}</td>
                  <td className="px-2 py-1">{item.splitUom ?? ''}</td>
                  <td className="px-2 py-1 text-slate-500">
                    {item.splitFormula ? `(${item.splitFormula})` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>

    {previewAtt && (
      <AttachmentPreviewModal
        url={previewAtt.url}
        filename={previewAtt.filename}
        onClose={() => setPreviewAtt(null)}
      />
    )}
  </>
  );
};
